import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Bike, Store, ShoppingBag, DollarSign, TrendingUp, Shield, LogOut,
  X, ChevronRight, Phone, MapPin, Star, Clock, Camera, Package, Utensils,
  CalendarDays, Award, Image as ImageIcon, Trash2, Plus, ExternalLink, Upload, MousePointerClick, Eye, EyeOff, AlertTriangle,
  Percent, Settings2, Save, FileText, Check,
} from "lucide-react";

const TABS = ["overview", "customers", "drivers", "restaurants", "orders", "delays", "settings", "pending-drivers", "banners"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = async () => {
    try {
      const [s, c, d, r, o] = await Promise.all([
        api.get("/admin/stats").then((x) => x.data),
        api.get("/admin/users?role=customer").then((x) => x.data),
        api.get("/admin/users?role=driver").then((x) => x.data),
        api.get("/restaurants").then((x) => x.data),
        api.get("/admin/orders").then((x) => x.data),
      ]);
      setStats(s); setCustomers(c); setDrivers(d); setRestaurants(r); setOrders(o);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  return (
    <div className="min-h-screen bg-eatup-gray flex">
      <aside className="w-64 shrink-0 bg-white border-r border-neutral-200 hidden lg:flex flex-col">
        <div className="p-6 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-11 h-11 rounded-2xl object-cover shadow-md" draggable={false} />
            <div>
              <div className="font-display text-lg font-bold">Eats GND</div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-eatup-green">Admin</div>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium capitalize transition ${
                tab === t ? "bg-eatup-green text-white" : "text-neutral-600 hover:bg-eatup-gray"
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout-btn" className="m-3 flex items-center gap-2 px-4 py-2.5 rounded-xl text-red-500 hover:bg-red-50 transition">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="flex-1 min-w-0 p-5 sm:p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold capitalize">{tab}</h1>
          <div className="text-sm text-neutral-500">Signed in as {user?.name}</div>
        </div>

        <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`pill shrink-0 capitalize ${tab === t ? "pill-active" : ""}`}>{t}</button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard testid="stat-customers" onClick={() => setTab("customers")} icon={Users} label="Customers" value={stats.customers} />
              <StatCard testid="stat-drivers" onClick={() => setTab("drivers")} icon={Bike} label="Drivers" value={stats.drivers} />
              <StatCard testid="stat-restaurants" onClick={() => setTab("restaurants")} icon={Store} label="Restaurants" value={stats.restaurants} />
              <StatCard testid="stat-orders" onClick={() => setTab("orders")} icon={ShoppingBag} label="Total orders" value={stats.orders} />
              <StatCard testid="stat-revenue" icon={DollarSign} label="Revenue (paid)" value={`$${stats.revenue.toFixed(2)}`} />
              <StatCard testid="stat-gmv" icon={TrendingUp} label="GMV" value={`$${stats.gmv.toFixed(2)}`} />
              <StatCard testid="stat-delivered" icon={ShoppingBag} label="Delivered" value={stats.delivered} />
            </div>

            <h2 className="mt-10 font-display text-2xl font-bold">Live orders</h2>
            <OrdersTable orders={orders.slice(0, 12)} onSelect={setSelectedOrder} />
          </>
        )}

        {tab === "customers" && <UsersTable users={customers} testidPrefix="customer" onSelect={setSelectedCustomer} />}
        {tab === "drivers" && <UsersTable users={drivers} testidPrefix="driver" onSelect={setSelectedDriver} />}
        {tab === "restaurants" && <RestaurantsTable restaurants={restaurants} onSelect={setSelectedRestaurant} />}
        {tab === "orders" && <OrdersTable orders={orders} onSelect={setSelectedOrder} />}
        {tab === "delays" && <DelayLogView />}
        {tab === "settings" && <MarketplaceSettingsView restaurants={restaurants} />}
        {tab === "pending-drivers" && <PendingDriversView />}
        {tab === "banners" && <BannersManager />}
      </main>

      {selectedCustomer && (
        <CustomerDetailsModal user={selectedCustomer} orders={orders.filter((o) => o.customer_id === selectedCustomer.user_id)}
          restaurants={restaurants} onClose={() => setSelectedCustomer(null)} onSelectOrder={setSelectedOrder}
        />
      )}
      {selectedDriver && (
        <DriverDetailsModal user={selectedDriver} orders={orders.filter((o) => o.driver_id === selectedDriver.user_id)}
          onClose={() => setSelectedDriver(null)} onSelectOrder={setSelectedOrder}
        />
      )}
      {selectedRestaurant && (
        <RestaurantDetailsModal restaurant={selectedRestaurant}
          orders={orders.filter((o) => o.restaurant_id === selectedRestaurant.restaurant_id)}
          onClose={() => setSelectedRestaurant(null)} onSelectOrder={setSelectedOrder}
        />
      )}
      {selectedOrder && (
        <AdminOrderDetailsModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, testid }) {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      onClick={onClick}
      data-testid={testid}
      className={`bg-white rounded-3xl card-shadow p-5 text-left w-full ${onClick ? "card-shadow-hover cursor-pointer" : ""}`}
    >
      <div className="w-10 h-10 rounded-xl bg-eatup-green/10 text-eatup-green flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold tabular">{value}</div>
    </Component>
  );
}

function UsersTable({ users, testidPrefix, onSelect }) {
  return (
    <div className="bg-white rounded-3xl card-shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-eatup-gray text-xs uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-5 py-3">Name</th>
            <th className="text-left px-5 py-3">Email</th>
            <th className="text-left px-5 py-3">Role</th>
            <th className="text-left px-5 py-3">Joined</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} data-testid={`${testidPrefix}-row-${u.user_id}`} onClick={() => onSelect(u)} className="border-t border-neutral-100 hover:bg-eatup-gray/50 cursor-pointer transition">
              <td className="px-5 py-3 font-semibold flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-eatup-charcoal text-white flex items-center justify-center text-xs font-bold overflow-hidden">
                  {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : (u.name || "?")[0].toUpperCase()}
                </div>
                {u.name}
              </td>
              <td className="px-5 py-3 text-neutral-500">{u.email}</td>
              <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-widest font-bold bg-eatup-green/10 text-eatup-green px-2 py-1 rounded-full">{u.role}</span></td>
              <td className="px-5 py-3 text-neutral-500 text-xs">{u.created_at?.slice(0, 10)}</td>
              <td className="px-5 py-3"><ChevronRight className="w-4 h-4 text-neutral-400" /></td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-neutral-500">No users yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function RestaurantsTable({ restaurants, onSelect }) {
  return (
    <div className="bg-white rounded-3xl card-shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-eatup-gray text-xs uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-5 py-3">Restaurant</th>
            <th className="text-left px-5 py-3">Cuisine</th>
            <th className="text-left px-5 py-3">Rating</th>
            <th className="text-left px-5 py-3">Delivery fee</th>
            <th className="text-left px-5 py-3">ETA</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {restaurants.map((r) => (
            <tr key={r.restaurant_id} data-testid={`admin-rest-${r.restaurant_id}`} onClick={() => onSelect(r)} className="border-t border-neutral-100 hover:bg-eatup-gray/50 cursor-pointer transition">
              <td className="px-5 py-3 font-semibold flex items-center gap-3">
                <img src={r.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                {r.name}
              </td>
              <td className="px-5 py-3 text-neutral-500">{r.categories?.join(", ")}</td>
              <td className="px-5 py-3">{r.rating} ⭐</td>
              <td className="px-5 py-3 tabular">${r.delivery_fee?.toFixed(2)}</td>
              <td className="px-5 py-3">{r.eta_minutes} min</td>
              <td className="px-5 py-3"><ChevronRight className="w-4 h-4 text-neutral-400" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersTable({ orders, onSelect }) {
  return (
    <div className="bg-white rounded-3xl card-shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-eatup-gray text-xs uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-5 py-3">Order</th>
            <th className="text-left px-5 py-3">Customer</th>
            <th className="text-left px-5 py-3">Restaurant</th>
            <th className="text-left px-5 py-3">Total</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-left px-5 py-3">Payment</th>
            <th className="text-left px-5 py-3">Proof</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`} onClick={() => onSelect(o)} className="border-t border-neutral-100 hover:bg-eatup-gray/50 cursor-pointer transition">
              <td className="px-5 py-3 font-mono text-xs">#{o.order_id.slice(-6).toUpperCase()}</td>
              <td className="px-5 py-3">{o.customer_name}</td>
              <td className="px-5 py-3">{o.restaurant_name}</td>
              <td className="px-5 py-3 tabular">${o.total.toFixed(2)}</td>
              <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-widest font-bold bg-neutral-100 text-neutral-700 px-2 py-1 rounded-full">{o.status.replace("_", " ")}</span></td>
              <td className="px-5 py-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-eatup-green/10 text-eatup-green" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span></td>
              <td className="px-5 py-3">
                <div className="flex gap-1">
                  {o.pickup_photo_image && <span className="w-6 h-6 rounded-md bg-eatup-yellow/30 text-eatup-charcoal flex items-center justify-center" title="Pickup photo"><Camera className="w-3 h-3" /></span>}
                  {o.proof_of_delivery_image && <span className="w-6 h-6 rounded-md bg-eatup-green/20 text-eatup-green flex items-center justify-center" title="Drop-off proof"><Camera className="w-3 h-3" /></span>}
                  {!o.pickup_photo_image && !o.proof_of_delivery_image && <span className="text-xs text-neutral-400">—</span>}
                </div>
              </td>
              <td className="px-5 py-3"><ChevronRight className="w-4 h-4 text-neutral-400" /></td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-neutral-500">No orders yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ModalShell({ children, onClose, testid, maxWidth = "sm:max-w-2xl" }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid={testid}>
      <div className={`bg-white text-eatup-charcoal w-full ${maxWidth} rounded-t-3xl sm:rounded-3xl overflow-hidden fade-up max-h-[92vh] flex flex-col shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose, icon: Icon, tint = "bg-eatup-charcoal text-eatup-yellow" }) {
  return (
    <div className="p-5 border-b border-neutral-100 flex items-center gap-4 shrink-0">
      {Icon && (
        <div className={`w-12 h-12 rounded-2xl ${tint} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-eatup-green font-bold">{subtitle}</div>
        <div className="font-display text-xl font-bold truncate">{title}</div>
      </div>
      <button onClick={onClose} className="w-10 h-10 rounded-full bg-eatup-gray hover:bg-neutral-200 flex items-center justify-center" data-testid="modal-close-btn">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function CustomerDetailsModal({ user, orders, restaurants, onClose, onSelectOrder }) {
  const totalSpent = orders.reduce((s, o) => s + (o.payment_status === "paid" ? o.total : 0), 0);
  const favIds = user.favorite_restaurants || [];
  const favs = restaurants.filter((r) => favIds.includes(r.restaurant_id));
  return (
    <ModalShell onClose={onClose} testid="customer-details-modal">
      <ModalHeader title={user.name} subtitle="Customer" onClose={onClose} icon={Users} />
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        <MetaGrid rows={[
          { label: "Email", value: user.email },
          { label: "Phone", value: user.phone || "—" },
          { label: "Address", value: user.address || "—" },
          { label: "Joined", value: user.created_at?.slice(0, 10) || "—" },
          { label: "Total spent", value: `$${totalSpent.toFixed(2)}` },
          { label: "Orders", value: orders.length },
        ]} />
        {favs.length > 0 && (
          <Section title="Favorite restaurants">
            <div className="grid gap-2">
              {favs.map((r) => (
                <div key={r.restaurant_id} className="flex items-center gap-3 border border-neutral-200 rounded-2xl p-3">
                  <img src={r.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.name}</div><div className="text-xs text-neutral-500">{r.categories?.join(", ")}</div></div>
                </div>
              ))}
            </div>
          </Section>
        )}
        <OrdersListSection orders={orders} onSelect={(o) => { onClose(); onSelectOrder(o); }} />
      </div>
    </ModalShell>
  );
}

function DriverDetailsModal({ user, orders, onClose, onSelectOrder }) {
  const delivered = orders.filter((o) => o.status === "delivered");
  const earnings = delivered.reduce((s, o) => s + o.delivery_fee + o.tip, 0);
  return (
    <ModalShell onClose={onClose} testid="driver-details-modal">
      <ModalHeader title={user.name} subtitle="Driver" onClose={onClose} icon={Bike} tint="bg-eatup-green/10 text-eatup-green" />
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        <MetaGrid rows={[
          { label: "Email", value: user.email },
          { label: "Status", value: user.online ? "🟢 Online" : "⚪ Offline" },
          { label: "Deliveries", value: delivered.length },
          { label: "Lifetime earnings", value: `$${earnings.toFixed(2)}` },
          { label: "Joined", value: user.created_at?.slice(0, 10) || "—" },
          { label: "Wallet", value: `$${(user.wallet_balance || 0).toFixed(2)}` },
        ]} />
        <OrdersListSection orders={orders} onSelect={(o) => { onClose(); onSelectOrder(o); }} label="Deliveries" />
      </div>
    </ModalShell>
  );
}

function RestaurantDetailsModal({ restaurant, orders, onClose, onSelectOrder }) {
  const [menu, setMenu] = useState([]);
  useEffect(() => {
    api.get(`/restaurants/${restaurant.restaurant_id}`).then((r) => setMenu(r.data.menu || [])).catch(() => {});
  }, [restaurant.restaurant_id]);
  const revenue = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + o.total, 0);
  const delivered = orders.filter((o) => o.status === "delivered").length;
  return (
    <ModalShell onClose={onClose} testid="restaurant-details-modal">
      <div className="relative shrink-0">
        <img src={restaurant.hero_url || restaurant.image_url} alt="" className="w-full h-32 sm:h-40 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <div className="text-[10px] uppercase tracking-widest text-eatup-yellow font-bold">Restaurant · {restaurant.categories?.join(" · ")}</div>
          <div className="font-display text-2xl font-bold">{restaurant.name}</div>
          <div className="text-xs text-white/70">{restaurant.tagline}</div>
        </div>
      </div>
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        <MetaGrid rows={[
          { label: "Address", value: restaurant.address },
          { label: "Rating", value: `${restaurant.rating} · ${restaurant.reviews} reviews` },
          { label: "Delivery fee", value: `$${restaurant.delivery_fee?.toFixed(2)}` },
          { label: "ETA", value: `${restaurant.eta_minutes} min` },
          { label: "Revenue (paid)", value: `$${revenue.toFixed(2)}` },
          { label: "Delivered", value: delivered },
        ]} />
        <Section title={`Menu (${menu.length})`}>
          <div className="grid gap-2 sm:grid-cols-2">
            {menu.slice(0, 12).map((m) => (
              <div key={m.item_id} className="flex items-center gap-3 border border-neutral-200 rounded-2xl p-3">
                <img src={m.image_url} alt="" className="w-11 h-11 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.name}</div>
                  <div className="text-xs text-neutral-500">${m.price.toFixed(2)} · {m.category}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
        <OrdersListSection orders={orders} onSelect={(o) => { onClose(); onSelectOrder(o); }} />
      </div>
    </ModalShell>
  );
}

function AdminOrderDetailsModal({ order, onClose }) {
  const timeline = order.timeline || [];
  return (
    <ModalShell onClose={onClose} testid="admin-order-details-modal">
      <ModalHeader title={`Order #${order.order_id.slice(-6).toUpperCase()}`} subtitle={order.status.replace("_", " ")} onClose={onClose} icon={ShoppingBag} tint="bg-eatup-green/10 text-eatup-green" />
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        {order.pickup_photo_image && (
          <PhotoCard label="Pickup photo (food left the kitchen)" image={order.pickup_photo_image} at={order.pickup_photo_at} tint="yellow" testid="admin-pickup-photo" />
        )}
        {order.proof_of_delivery_image && (
          <PhotoCard label="Proof of delivery (drop-off)" image={order.proof_of_delivery_image} at={order.proof_captured_at} tint="green" testid="admin-proof-photo" />
        )}
        <MetaGrid rows={[
          { label: "Customer", value: `${order.customer_name} (${order.customer_phone})` },
          { label: "Restaurant", value: order.restaurant_name },
          { label: "Address", value: order.address },
          { label: "Payment", value: `${order.payment_method} · ${order.payment_status}` },
          { label: "Pickup verified", value: order.pickup_verified ? "✅ Yes" : "—" },
          { label: "Pickup code", value: order.pickup_code || "—" },
        ]} />
        <Section title="Items">
          <div className="border border-neutral-200 rounded-3xl divide-y divide-neutral-100">
            {order.items.map((it) => (
              <div key={it.item_id} className="p-3 flex items-center gap-3">
                {it.image_url && <img src={it.image_url} alt="" className="w-11 h-11 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{it.quantity}× {it.name}</div>
                  {it.notes && <div className="text-xs text-neutral-500">Note: {it.notes}</div>}
                </div>
                <div className="text-sm tabular font-semibold">${(it.price * it.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Bill">
          <div className="border border-neutral-200 rounded-3xl p-4 space-y-1 text-sm">
            <Row label="Subtotal" value={`$${order.subtotal.toFixed(2)}`} />
            <Row label="Delivery" value={`$${order.delivery_fee.toFixed(2)}`} />
            <Row label="Service" value={`$${order.service_charge.toFixed(2)}`} />
            <Row label="Tax" value={`$${order.tax.toFixed(2)}`} />
            <Row label="Tip" value={`$${order.tip.toFixed(2)}`} />
            {order.discount > 0 && <Row label={`Promo ${order.promo_code}`} value={`- $${order.discount.toFixed(2)}`} accent />}
            <div className="h-px bg-neutral-100 my-1" />
            <Row label="Total" value={`$${order.total.toFixed(2)}`} bold />
          </div>
        </Section>
        {timeline.length > 0 && (
          <Section title="Timeline">
            <div className="border border-neutral-200 rounded-3xl p-4 space-y-2">
              {timeline.map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className={`w-2 h-2 rounded-full ${i === timeline.length - 1 ? "bg-eatup-green" : "bg-neutral-300"}`} />
                  <div className="flex-1 font-medium capitalize">{t.status.replace("_", " ")}</div>
                  <div className="text-xs text-neutral-500">{new Date(t.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </ModalShell>
  );
}

function PhotoCard({ label, image, at, tint = "green", testid }) {
  const tintClass = tint === "yellow" ? "border-eatup-yellow/40 bg-eatup-yellow/10" : "border-eatup-green/40 bg-eatup-green/5";
  const iconClass = tint === "yellow" ? "text-eatup-charcoal" : "text-eatup-green";
  return (
    <div className={`rounded-3xl overflow-hidden border ${tintClass}`} data-testid={testid}>
      <img src={image} alt={label} className="w-full aspect-video object-cover" />
      <div className="p-3 flex items-center gap-2 text-xs">
        <Camera className={`w-3.5 h-3.5 ${iconClass}`} />
        <span className="font-semibold">{label}</span>
        {at && <span className="text-neutral-400 ml-auto">{new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function MetaGrid({ rows }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map((r, i) => (
        <div key={i} className="border border-neutral-200 rounded-2xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{r.label}</div>
          <div className="mt-0.5 font-semibold text-sm truncate">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function OrdersListSection({ orders, onSelect, label = "Orders" }) {
  if (!orders.length) return (
    <Section title={label}>
      <div className="border border-neutral-200 rounded-2xl p-4 text-center text-sm text-neutral-500">No orders yet</div>
    </Section>
  );
  return (
    <Section title={`${label} (${orders.length})`}>
      <div className="space-y-2">
        {orders.slice(0, 10).map((o) => (
          <button key={o.order_id} onClick={() => onSelect(o)} data-testid={`sub-order-${o.order_id}`} className="w-full flex items-center gap-3 border border-neutral-200 rounded-2xl p-3 hover:bg-eatup-gray text-left transition">
            <img src={o.restaurant_image} alt="" className="w-11 h-11 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{o.restaurant_name}</div>
              <div className="text-xs text-neutral-500">#{o.order_id.slice(-6).toUpperCase()} · {o.items.length} items · ${o.total.toFixed(2)}</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold bg-eatup-green/10 text-eatup-green px-2 py-1 rounded-full">{o.status.replace("_", " ")}</span>
            <ChevronRight className="w-4 h-4 text-neutral-400" />
          </button>
        ))}
      </div>
    </Section>
  );
}

function Row({ label, value, accent, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-bold text-eatup-charcoal" : "text-neutral-500"}`}>{label}</span>
      <span className={`tabular ${accent ? "text-eatup-green font-semibold" : bold ? "font-bold" : "font-semibold"}`}>{value}</span>
    </div>
  );
}


// ---------------------------------------------------------------
// Banner Ads management (Admin > Banners tab)
// ---------------------------------------------------------------
function BannersManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // banner being edited or "new"
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/banners");
      setBanners(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this banner? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success("Banner deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalClicks = banners.reduce((s, b) => s + (b.clicks || 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const liveCount = banners.filter((b) => b.active && (!b.start_date || b.start_date <= today) && (!b.end_date || b.end_date >= today)).length;

  return (
    <div data-testid="admin-banners">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <StatMini icon={ImageIcon} label="Total banners" value={banners.length} />
        <StatMini icon={Eye} label="Live today" value={liveCount} accent="text-eatup-green" />
        <StatMini icon={MousePointerClick} label="Clicks to date" value={totalClicks} accent="text-eatup-yellow" />
        <div className="flex-1" />
        <button
          data-testid="admin-banner-new-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-eatup-charcoal text-white rounded-full px-5 py-2.5 font-semibold hover:bg-black transition"
        >
          <Plus className="w-4 h-4" /> New banner
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading banners…</div>
      ) : banners.length === 0 ? (
        <div className="rounded-3xl bg-white border border-neutral-200 p-10 text-center text-neutral-500">
          No banners yet. Click "New banner" to create your first sponsored slide.
        </div>
      ) : (
        <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                <th className="text-left px-4 py-3">Preview</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Link</th>
                <th className="text-left px-4 py-3">Dates</th>
                <th className="text-right px-4 py-3">Clicks</th>
                <th className="text-right px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {banners.map((b) => {
                const live = b.active && (!b.start_date || b.start_date <= today) && (!b.end_date || b.end_date >= today);
                return (
                  <tr key={b.banner_id} data-testid={`admin-banner-row-${b.banner_id}`} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <img src={b.image_url} alt={b.title} className="w-24 h-14 rounded-lg object-cover ring-1 ring-black/5" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-eatup-charcoal">{b.title || <span className="italic text-neutral-400">(untitled)</span>}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[220px] truncate">
                      {b.link_url ? (
                        <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="text-eatup-green hover:underline inline-flex items-center gap-1">
                          {b.link_url} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-600">
                      <div>{b.start_date} → {b.end_date}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold text-eatup-yellow tabular" data-testid={`admin-banner-clicks-${b.banner_id}`}>
                      {b.clicks || 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
                        live ? "bg-eatup-green/15 text-eatup-green" : "bg-neutral-200 text-neutral-500"
                      }`}>
                        {live ? "Live" : "Off"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setEditing(b); setShowForm(true); }}
                        data-testid={`admin-banner-edit-${b.banner_id}`}
                        className="text-sm font-semibold text-eatup-charcoal hover:text-eatup-green px-3 py-1.5 rounded-full hover:bg-eatup-gray transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(b.banner_id)}
                        data-testid={`admin-banner-delete-${b.banner_id}`}
                        className="text-sm font-semibold text-red-600 hover:text-red-700 px-3 py-1.5 rounded-full hover:bg-red-50 transition inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <BannerForm
          banner={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BannerForm({ banner, onClose, onSaved }) {
  const isEdit = !!banner;
  const [title, setTitle] = useState(banner?.title || "");
  const [linkUrl, setLinkUrl] = useState(banner?.link_url || "");
  const [imageUrl, setImageUrl] = useState(banner?.image_url || "");
  const [startDate, setStartDate] = useState(banner?.start_date || new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(banner?.end_date || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [active, setActive] = useState(banner ? banner.active !== false : true);
  const [saving, setSaving] = useState(false);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!imageUrl) { toast.error("Please upload an image or paste an image URL"); return; }
    if (!startDate || !endDate) { toast.error("Start and end dates are required"); return; }
    if (endDate < startDate) { toast.error("End date can't be earlier than start date"); return; }
    setSaving(true);
    try {
      const payload = {
        title, image_url: imageUrl, link_url: linkUrl,
        start_date: startDate, end_date: endDate, active,
      };
      if (isEdit) {
        await api.put(`/admin/banners/${banner.banner_id}`, payload);
        toast.success("Banner updated");
      } else {
        await api.post("/admin/banners", payload);
        toast.success("Banner created");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="banner-form-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-eatup-green">{isEdit ? "Edit" : "New"} banner</div>
            <h3 className="font-display font-black text-xl">{title || "Untitled banner"}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-eatup-gray hover:bg-neutral-200 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1.5">Title (optional)</div>
            <input
              data-testid="banner-form-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 20% off tonight only"
              className="w-full px-4 py-2.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
            />
          </label>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1.5">Image</div>
            {imageUrl ? (
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-black/5">
                <img src={imageUrl} alt="Preview" className="w-full aspect-[3/1] object-cover" data-testid="banner-form-preview" />
                <button onClick={() => setImageUrl("")} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white hover:bg-black flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-300 hover:border-eatup-green px-4 py-6 cursor-pointer transition">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-semibold">Upload image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="banner-form-file"
                  />
                </label>
                <input
                  data-testid="banner-form-image-url"
                  value=""
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="…or paste an image URL"
                  className="w-full px-4 py-2.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none text-sm"
                />
              </div>
            )}
          </div>

          <label className="block">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1.5">Link URL</div>
            <input
              data-testid="banner-form-link"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://…"
              className="w-full px-4 py-2.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1.5">Start date</div>
              <input
                type="date"
                data-testid="banner-form-start"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
              />
            </label>
            <label className="block">
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1.5">End date</div>
              <input
                type="date"
                data-testid="banner-form-end"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
              />
            </label>
          </div>

          <label className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-eatup-gray cursor-pointer hover:bg-neutral-100 transition">
            <input
              type="checkbox"
              data-testid="banner-form-active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4 accent-eatup-green"
            />
            <span className="text-sm font-semibold">Active</span>
            <span className="text-xs text-neutral-500 ml-auto">Uncheck to hide the banner without deleting it</span>
          </label>
        </div>

        <div className="p-5 border-t border-neutral-100 flex items-center gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-full bg-eatup-gray hover:bg-neutral-200 font-semibold text-sm transition">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="banner-form-save"
            className="flex-1 py-3 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white font-display font-bold transition disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create banner"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl px-4 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-eatup-gray flex items-center justify-center">
        <Icon className="w-4 h-4 text-eatup-charcoal" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
        <div className={`font-display font-black text-xl tabular ${accent || ""}`}>{value}</div>
      </div>
    </div>
  );
}

function DelayLogView() {
  const [period, setPeriod] = useState("week");
  const [autoOnly, setAutoOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/admin/delay-logs?period=${period}&auto_only=${autoOnly}`);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, autoOnly]);

  return (
    <section data-testid="delay-log-view" className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-full bg-white border border-neutral-200 p-1 shadow-sm">
          {["week", "month"].map((p) => (
            <button
              key={p}
              data-testid={`delay-period-${p}`}
              onClick={() => setPeriod(p)}
              className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full transition ${
                period === p ? "bg-eatup-charcoal text-white" : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              Last {p === "week" ? "7 days" : "30 days"}
            </button>
          ))}
        </div>
        <button
          data-testid="delay-auto-only-toggle"
          onClick={() => setAutoOnly((v) => !v)}
          className={`text-xs font-bold px-4 py-2 rounded-full border transition ${
            autoOnly
              ? "bg-eatup-yellow/20 border-eatup-yellow text-eatup-charcoal"
              : "bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50"
          }`}
        >
          {autoOnly ? "Auto-check confirmations only" : "All delay entries"}
        </button>
      </div>

      {loading && (
        <div className="p-10 text-center text-neutral-400 bg-white border border-neutral-200 rounded-3xl" data-testid="delay-log-loading">
          Loading delay log…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary tiles */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatMini icon={AlertTriangle} label="Incidents" value={data.totals.incidents} accent="text-red-600" />
            <StatMini icon={Clock} label="Auto-triggered" value={data.totals.auto_incidents} accent="text-amber-600" />
            <StatMini icon={Store} label="Restaurants affected" value={data.totals.restaurants_affected} />
            <StatMini icon={TrendingUp} label="Total minutes lost" value={`${data.totals.total_minutes}m`} accent="text-eatup-charcoal" />
          </div>

          {/* Per-restaurant leaderboard */}
          <div>
            <h2 className="font-display text-xl font-bold mb-3">Performance by restaurant</h2>
            {data.by_restaurant.length === 0 ? (
              <div className="p-10 text-center text-neutral-400 bg-white border border-neutral-200 rounded-3xl" data-testid="delay-log-empty">
                Nothing logged in the last {data.days} days — kitchens are on time.
              </div>
            ) : (
              <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
                <div className="hidden md:grid grid-cols-[1.4fr_repeat(4,1fr)_auto] gap-3 px-5 py-3 border-b border-neutral-100 text-[10px] uppercase tracking-widest text-neutral-500 font-black">
                  <div>Restaurant</div>
                  <div className="text-right">Incidents</div>
                  <div className="text-right">Auto-check</div>
                  <div className="text-right">Orders</div>
                  <div className="text-right">Avg delay</div>
                  <div className="text-right">Last flagged</div>
                </div>
                {data.by_restaurant.map((row) => (
                  <div
                    key={row.restaurant_id}
                    data-testid={`delay-leaderboard-row-${row.restaurant_id}`}
                    className="grid grid-cols-2 md:grid-cols-[1.4fr_repeat(4,1fr)_auto] gap-3 items-center px-5 py-4 border-b border-neutral-100 last:border-b-0"
                  >
                    <div className="min-w-0 col-span-2 md:col-span-1">
                      <div className="font-display font-bold truncate">{row.restaurant_name}</div>
                      <div className="text-xs text-neutral-500 truncate">{row.restaurant_id}</div>
                    </div>
                    <div className="md:text-right text-sm tabular font-bold text-red-600">
                      <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Incidents</span>
                      {row.incidents}
                    </div>
                    <div className="md:text-right text-sm tabular font-bold text-amber-600">
                      <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Auto</span>
                      {row.auto_incidents}
                    </div>
                    <div className="md:text-right text-sm tabular text-neutral-700">
                      <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Orders</span>
                      {row.orders_affected}
                    </div>
                    <div className="md:text-right text-sm tabular text-neutral-700">
                      <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Avg</span>
                      {row.avg_delay_minutes} min
                    </div>
                    <div className="md:text-right text-xs text-neutral-500 whitespace-nowrap col-span-2 md:col-span-1">
                      {row.last_at ? new Date(row.last_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Full log */}
          {data.logs.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold mb-3">All delay entries</h2>
              <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
                {data.logs.map((entry) => (
                  <div
                    key={entry.log_id}
                    data-testid={`delay-log-entry-${entry.log_id}`}
                    className="grid grid-cols-[auto_1fr_auto] gap-4 items-center px-5 py-4 border-b border-neutral-100 last:border-b-0"
                  >
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${entry.auto_triggered ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {entry.auto_triggered ? <Clock className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold truncate">{entry.restaurant_name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-eatup-green font-bold tabular">
                          #{(entry.order_id || "").slice(-6).toUpperCase()}
                        </span>
                        {entry.auto_triggered && (
                          <span className="text-[10px] uppercase tracking-widest font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Auto-check
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {entry.customer_name || "—"} · added +{entry.delay_minutes} min · new ETA {entry.new_eta_minutes} min
                        {entry.reason ? ` · ${entry.reason}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-neutral-500 tabular whitespace-nowrap">
                        {new Date(entry.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
                        by {entry.reported_by_name || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}


function MarketplaceSettingsView({ restaurants }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platform_fee_pct: 15,
    base_delivery_fee: 7.99,
    delivery_per_km: 1.75,
    delivery_free_km: 3,
    platform_flat_fee: 2.5,
    small_order_threshold: 20,
    small_order_fee: 3,
    driver_base_pay: 8,
    driver_per_km_pay: 1.5,
  });
  const [overrides, setOverrides] = useState([]);
  const [overrideModal, setOverrideModal] = useState(null); // { restaurant_id, restaurant_name, platform_fee_pct }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketplace/settings");
      setSettings((s) => ({ ...s, ...data.settings }));
      setOverrides(data.restaurant_overrides || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      const patch = {};
      for (const k of [
        "platform_fee_pct", "base_delivery_fee", "delivery_per_km", "delivery_free_km",
        "platform_flat_fee", "small_order_threshold", "small_order_fee",
        "driver_base_pay", "driver_per_km_pay",
      ]) {
        patch[k] = Number(settings[k]);
      }
      const { data } = await api.put("/admin/marketplace/settings", patch);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Marketplace settings saved. All new orders will use these rates.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const saveOverride = async () => {
    if (!overrideModal) return;
    setSaving(true);
    try {
      await api.put(`/admin/marketplace/settings/restaurants/${overrideModal.restaurant_id}`, {
        platform_fee_pct: Number(overrideModal.platform_fee_pct),
      });
      toast.success(`Override saved for ${overrideModal.restaurant_name}`);
      setOverrideModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const removeOverride = async (rid, name) => {
    if (!window.confirm(`Remove custom platform fee for ${name}? They'll fall back to the global rate.`)) return;
    try {
      await api.delete(`/admin/marketplace/settings/restaurants/${rid}`);
      toast.success("Override removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-neutral-400 bg-white border border-neutral-200 rounded-3xl" data-testid="settings-loading">
        Loading marketplace settings…
      </div>
    );
  }

  return (
    <section data-testid="marketplace-settings-view" className="space-y-6">
      <div className="rounded-3xl bg-eatup-green/10 border border-eatup-green/30 p-4 text-sm text-eatup-green-dark flex items-start gap-3">
        <Settings2 className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          These numbers drive every earnings, commission and payout calculation across the platform.
          Changes apply to <span className="font-black">new</span> orders — existing orders keep the fees they were placed with.
        </div>
      </div>

      {/* Global rates */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-5 sm:p-6 space-y-5">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Percent className="w-5 h-5 text-eatup-green" /> Global rates
        </h2>

        {/* Restaurant commission */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-2">Restaurant commission</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <SettingField
              testid="settings-platform-fee"
              icon={Store}
              label="Restaurant commission"
              hint="% of subtotal charged to the restaurant."
              suffix="%"
              value={settings.platform_fee_pct}
              onChange={(v) => setSettings((s) => ({ ...s, platform_fee_pct: v }))}
              min={0} max={50} step={0.5}
            />
          </div>
        </div>

        {/* Customer fees */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-2">Customer fees (EC$)</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <SettingField
              testid="settings-base-delivery"
              icon={DollarSign}
              label="Base delivery fee"
              hint="Flat fee for any delivery."
              prefix="EC$"
              value={settings.base_delivery_fee}
              onChange={(v) => setSettings((s) => ({ ...s, base_delivery_fee: v }))}
              min={0} max={100} step={0.1}
            />
            <SettingField
              testid="settings-delivery-per-km"
              icon={MapPin}
              label="Per-km rate"
              hint="Applied to distance above the free-km threshold."
              prefix="EC$"
              value={settings.delivery_per_km}
              onChange={(v) => setSettings((s) => ({ ...s, delivery_per_km: v }))}
              min={0} max={20} step={0.05}
            />
            <SettingField
              testid="settings-delivery-free-km"
              icon={MapPin}
              label="Free km"
              hint="km included before per-km rate applies."
              suffix="km"
              value={settings.delivery_free_km}
              onChange={(v) => setSettings((s) => ({ ...s, delivery_free_km: v }))}
              min={0} max={50} step={0.5}
            />
            <SettingField
              testid="settings-platform-flat-fee"
              icon={Shield}
              label="Platform fee"
              hint="Flat fee added to every order."
              prefix="EC$"
              value={settings.platform_flat_fee}
              onChange={(v) => setSettings((s) => ({ ...s, platform_flat_fee: v }))}
              min={0} max={20} step={0.1}
            />
            <SettingField
              testid="settings-small-order-threshold"
              icon={ShoppingBag}
              label="Small-order threshold"
              hint="Orders under this amount pay the small-order fee."
              prefix="EC$"
              value={settings.small_order_threshold}
              onChange={(v) => setSettings((s) => ({ ...s, small_order_threshold: v }))}
              min={0} max={200} step={0.5}
            />
            <SettingField
              testid="settings-small-order-fee"
              icon={ShoppingBag}
              label="Small-order fee"
              hint="Added when subtotal is under the threshold."
              prefix="EC$"
              value={settings.small_order_fee}
              onChange={(v) => setSettings((s) => ({ ...s, small_order_fee: v }))}
              min={0} max={20} step={0.1}
            />
          </div>
        </div>

        {/* Driver pay */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-2">Driver pay (EC$) · Tips 100% to driver</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <SettingField
              testid="settings-driver-base-pay"
              icon={Bike}
              label="Base pay per trip"
              hint="Paid to the driver on every accepted trip."
              prefix="EC$"
              value={settings.driver_base_pay}
              onChange={(v) => setSettings((s) => ({ ...s, driver_base_pay: v }))}
              min={0} max={50} step={0.25}
            />
            <SettingField
              testid="settings-driver-per-km-pay"
              icon={Bike}
              label="Distance pay per km"
              hint="Multiplied by trip distance from pickup to dropoff."
              prefix="EC$"
              value={settings.driver_per_km_pay}
              onChange={(v) => setSettings((s) => ({ ...s, driver_per_km_pay: v }))}
              min={0} max={20} step={0.05}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            data-testid="settings-save-btn"
            onClick={saveGlobal}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-eatup-charcoal hover:bg-black text-white font-black uppercase tracking-widest text-xs transition disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save global rates"}
          </button>
        </div>
      </div>

      {/* Restaurant-specific overrides */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-5 sm:p-6 space-y-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Store className="w-5 h-5 text-eatup-yellow" /> Restaurant-specific fees
            </h2>
            <div className="text-xs text-neutral-500 mt-1">
              Override the global platform fee for a specific restaurant.
            </div>
          </div>
          <button
            data-testid="settings-add-override-btn"
            onClick={() => setOverrideModal({ restaurant_id: "", restaurant_name: "", platform_fee_pct: settings.platform_fee_pct })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white font-black uppercase tracking-widest text-xs transition"
          >
            <Plus className="w-4 h-4" /> Add override
          </button>
        </div>

        {overrides.length === 0 ? (
          <div className="p-8 text-center text-neutral-400 border border-dashed border-neutral-200 rounded-2xl" data-testid="settings-overrides-empty">
            No restaurant overrides yet — every restaurant pays the global rate ({settings.platform_fee_pct}%).
          </div>
        ) : (
          <div className="border border-neutral-100 rounded-2xl overflow-hidden">
            <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_auto] gap-3 px-4 py-2.5 bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500 font-black">
              <div>Restaurant</div>
              <div className="text-right">Platform fee</div>
              <div className="text-right">Updated</div>
              <div />
            </div>
            {overrides.map((row) => (
              <div
                key={row.restaurant_id}
                data-testid={`settings-override-row-${row.restaurant_id}`}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 items-center px-4 py-3 border-t border-neutral-100"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">{row.restaurant_name}</div>
                  <div className="text-xs text-neutral-500 truncate">{row.restaurant_id}</div>
                </div>
                <div className="hidden sm:block text-right font-display font-black tabular text-lg text-eatup-yellow">
                  {row.platform_fee_pct}%
                </div>
                <div className="hidden sm:block text-right text-xs text-neutral-500 whitespace-nowrap">
                  {row.updated_at ? new Date(row.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid={`settings-override-edit-${row.restaurant_id}`}
                    onClick={() => setOverrideModal({ restaurant_id: row.restaurant_id, restaurant_name: row.restaurant_name, platform_fee_pct: row.platform_fee_pct })}
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 transition"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`settings-override-remove-${row.restaurant_id}`}
                    onClick={() => removeOverride(row.restaurant_id, row.restaurant_name)}
                    className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition"
                    aria-label="Remove override"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {overrideModal && (
        <OverrideEditorModal
          modal={overrideModal}
          onChange={setOverrideModal}
          onSave={saveOverride}
          onClose={() => setOverrideModal(null)}
          saving={saving}
          restaurants={restaurants}
          existingIds={overrides.map((o) => o.restaurant_id)}
        />
      )}
    </section>
  );
}

function SettingField({ testid, icon: Icon, label, hint, prefix, suffix, value, onChange, min, max, step }) {
  return (
    <label className="block bg-neutral-50 border border-neutral-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-black">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 flex items-center gap-2">
        {prefix && <span className="font-display font-black text-3xl text-neutral-400">{prefix}</span>}
        <input
          data-testid={testid}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent font-display font-black text-3xl tabular focus:outline-none focus:ring-0"
        />
        {suffix && <span className="font-display font-black text-2xl text-neutral-400">{suffix}</span>}
      </div>
      {hint && <div className="mt-1 text-xs text-neutral-500">{hint}</div>}
    </label>
  );
}

function OverrideEditorModal({ modal, onChange, onSave, onClose, saving, restaurants, existingIds }) {
  const isNew = !modal.restaurant_id;
  const available = (restaurants || []).filter((r) => !existingIds.includes(r.restaurant_id));
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="settings-override-modal">
      <div className="bg-white text-eatup-charcoal w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-eatup-green font-black">Restaurant override</div>
            <h3 className="font-display text-xl font-black mt-0.5">
              {isNew ? "Add a custom fee" : modal.restaurant_name}
            </h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {isNew && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-1.5">Restaurant</div>
              <select
                data-testid="settings-override-restaurant-select"
                value={modal.restaurant_id}
                onChange={(e) => {
                  const rid = e.target.value;
                  const rest = available.find((r) => r.restaurant_id === rid);
                  onChange({ ...modal, restaurant_id: rid, restaurant_name: rest?.name || "" });
                }}
                className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-eatup-green/30"
              >
                <option value="">Choose a restaurant…</option>
                {available.map((r) => (
                  <option key={r.restaurant_id} value={r.restaurant_id}>{r.name}</option>
                ))}
              </select>
              {available.length === 0 && (
                <div className="text-[11px] text-neutral-500 mt-1">Every restaurant already has an override.</div>
              )}
            </label>
          )}

          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-1.5">Platform fee</div>
            <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3">
              <input
                data-testid="settings-override-fee-input"
                type="number"
                value={modal.platform_fee_pct}
                min={0}
                max={50}
                step={0.5}
                onChange={(e) => onChange({ ...modal, platform_fee_pct: e.target.value })}
                className="w-full bg-transparent font-display font-black text-3xl tabular focus:outline-none"
              />
              <span className="font-display font-black text-2xl text-neutral-400">%</span>
            </div>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full text-sm font-bold text-neutral-600 hover:bg-neutral-100 transition">
            Cancel
          </button>
          <button
            data-testid="settings-override-save-btn"
            onClick={onSave}
            disabled={saving || !modal.restaurant_id || modal.platform_fee_pct === "" || modal.platform_fee_pct == null}
            className="px-5 py-2.5 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white font-black uppercase tracking-widest text-xs transition disabled:opacity-60 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save override"}
          </button>
        </div>
      </div>
    </div>
  );
}


const DOC_LABELS = {
  drivers_license:       "Driver's License",
  national_id:           "National ID / Passport",
  police_certificate:    "Police Certificate of Character",
  proof_of_address:      "Proof of Address",
  vehicle_registration:  "Vehicle Registration",
  insurance_certificate: "Insurance Certificate",
  vehicle_inspection:    "Vehicle Inspection",
  profile_photo:         "Profile Photo",
  selfie_verification:   "Selfie Verification",
};

function PendingDriversView() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drivers/pending");
      setDrivers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (driverId) => {
    try {
      const { data } = await api.get(`/admin/drivers/${driverId}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/drivers/${selected.user_id}/approve`, { notes: "" });
      toast.success(`Approved ${selected.name}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (docs, notes) => {
    if (!selected) return;
    try {
      await api.post(`/admin/drivers/${selected.user_id}/reject`, { notes, documents_to_reupload: docs });
      toast.success(`${selected.name} — flagged ${docs.length} document${docs.length === 1 ? "" : "s"} for re-upload`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="pending-drivers-view" className="space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Bike className="w-5 h-5 text-eatup-green" /> Pending drivers
          </h2>
          <div className="text-xs text-neutral-500 mt-0.5">
            {drivers.length} driver{drivers.length === 1 ? "" : "s"} waiting for review
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-neutral-400 bg-white border border-neutral-200 rounded-3xl">Loading…</div>
      ) : drivers.length === 0 ? (
        <div className="p-10 text-center text-neutral-400 bg-white border border-neutral-200 rounded-3xl" data-testid="pending-drivers-empty">
          No drivers awaiting review.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
          {drivers.map((d) => (
            <button
              key={d.user_id}
              data-testid={`pending-driver-row-${d.user_id}`}
              onClick={() => openDetail(d.user_id)}
              className="w-full text-left px-5 py-4 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-eatup-charcoal text-white flex items-center justify-center font-bold shrink-0">
                {(d.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{d.name}</div>
                <div className="text-xs text-neutral-500 truncate">{d.email}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black">Docs</div>
                <div className="font-display font-black tabular text-sm">
                  {d.documents_uploaded_count}/{d.documents_total}
                </div>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${
                d.approval_status === "pending_review"
                  ? "bg-eatup-yellow/20 text-amber-700"
                  : "bg-neutral-100 text-neutral-500"
              }`}>
                {d.approval_status === "pending_review" ? "Ready to review" : "Incomplete"}
              </span>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <DriverReviewModal
          driver={selected}
          onClose={() => setSelected(null)}
          onApprove={approve}
          onReject={reject}
        />
      )}
    </section>
  );
}

function DriverReviewModal({ driver, onClose, onApprove, onReject }) {
  const canApprove = driver.approval_status === "pending_review";
  const docs = driver.driver_documents || {};
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reuploadDocs, setReuploadDocs] = useState([]);
  const [rejectNotes, setRejectNotes] = useState("");
  const toggleDoc = (k) => setReuploadDocs((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  const submitReject = () => {
    if (reuploadDocs.length === 0) {
      toast.error("Pick at least one document to re-request");
      return;
    }
    onReject(reuploadDocs, rejectNotes.trim());
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="driver-review-modal">
      <div className="bg-white text-eatup-charcoal w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-eatup-green font-black">Driver review</div>
            <h3 className="font-display text-2xl font-black">{driver.name}</h3>
            <div className="text-xs text-neutral-500">{driver.email}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Documents grid */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-2">Documents</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.entries(DOC_LABELS).map(([key, label]) => {
                const path = docs[key];
                return (
                  <a
                    key={key}
                    href={path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}` : undefined}
                    target={path ? "_blank" : undefined}
                    rel="noreferrer"
                    onClick={(e) => { if (!path) e.preventDefault(); }}
                    data-testid={`review-doc-${key}`}
                    className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition ${
                      path
                        ? "border-eatup-green/40 bg-eatup-green/5 hover:bg-eatup-green/10"
                        : "border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${path ? "bg-eatup-green text-white" : "bg-neutral-200"}`}>
                      {path ? <Check className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{label}</div>
                      <div className="text-[11px] truncate">{path ? "View file" : "Missing"}</div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Emergency contact */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black">Emergency contact</div>
              <div className="mt-1 font-semibold" data-testid="review-emergency-name">
                {driver.emergency_contact?.name || "—"}
              </div>
              <div className="text-xs text-neutral-500">
                {driver.emergency_contact?.phone || ""}
                {driver.emergency_contact?.relationship ? ` · ${driver.emergency_contact.relationship}` : ""}
              </div>
            </div>
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black">Bank details</div>
              <div className="mt-1 font-semibold" data-testid="review-bank-account-name">{driver.bank_details?.account_name || "—"}</div>
              <div className="text-xs text-neutral-500">
                {driver.bank_details?.bank_name || ""}
                {driver.bank_details?.account_number ? ` · ****${String(driver.bank_details.account_number).slice(-4)}` : ""}
              </div>
            </div>
          </div>

          {/* Agreements + training */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-neutral-200 p-4 space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-1">Legal agreements</div>
              <AgreementRow label="Independent Contractor" ok={driver.legal_agreements?.independent_contractor} />
              <AgreementRow label="Food Safety" ok={driver.legal_agreements?.food_safety} />
              <AgreementRow label="Code of Conduct" ok={driver.legal_agreements?.code_of_conduct} />
            </div>
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-1">Training</div>
              <AgreementRow label="Watched full training video" ok={driver.training?.watched_video} />
              <div className="mt-2 text-[11px] text-neutral-500 truncate">
                {driver.training?.watched_at ? `Confirmed ${new Date(driver.training.watched_at).toLocaleString()}` : "Not confirmed"}
              </div>
            </div>
          </div>
        </div>

        {rejectOpen && (
          <div className="px-6 py-4 bg-red-50 border-t border-red-100" data-testid="reject-panel">
            <div className="text-[10px] uppercase tracking-widest text-red-700 font-black mb-2">
              Which documents need to be re-uploaded?
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2 mb-3">
              {Object.entries(DOC_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reuploadDocs.includes(key)}
                    onChange={() => toggleDoc(key)}
                    data-testid={`reject-doc-${key}`}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Notes for the driver (optional) — e.g. 'License photo was blurry'"
              data-testid="reject-notes-input"
              rows={2}
              className="w-full rounded-xl border border-red-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={() => { setRejectOpen(false); setReuploadDocs([]); setRejectNotes(""); }}
                className="px-4 py-2 rounded-full text-xs font-bold text-neutral-600 hover:bg-white transition"
              >
                Cancel
              </button>
              <button
                data-testid="reject-confirm-btn"
                onClick={submitReject}
                className="px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-black uppercase tracking-widest transition"
              >
                Send rejection ({reuploadDocs.length})
              </button>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-neutral-100 flex items-center gap-2 shrink-0">
          <button
            data-testid="review-reject-btn"
            onClick={() => setRejectOpen((v) => !v)}
            className={`px-5 py-3 rounded-full text-sm font-black uppercase tracking-widest transition border ${
              rejectOpen
                ? "bg-red-600 text-white border-red-600"
                : "text-red-600 hover:bg-red-50 border-red-200"
            }`}
          >
            {rejectOpen ? "Hide reject" : "Reject"}
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-4 py-3 rounded-full text-sm font-bold text-neutral-600 hover:bg-neutral-100 transition"
          >
            Close
          </button>
          <button
            data-testid="review-approve-btn"
            onClick={onApprove}
            disabled={!canApprove}
            className="px-6 py-3 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white font-black uppercase tracking-widest text-xs transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {canApprove ? "Approve driver" : "Docs incomplete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgreementRow({ label, ok }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <Check className="w-3.5 h-3.5 text-eatup-green shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
      )}
      <span className={ok ? "text-eatup-charcoal" : "text-neutral-400"}>{label}</span>
    </div>
  );
}

